import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChevronRight, FaExternalLinkAlt } from "react-icons/fa";
import { bgheader, olheader } from "../assets";
import {
  Footer,
  // Header,
  Navbar,
  SiluetCard,
  MarkdownParser,
} from "../components";
import { useQuery } from "@tanstack/react-query";
import ErrorPage from "./ErrorPage";
import { GenericMarkdownLoadingSkeleton } from "../components/LoadingSkeleton";
import customAxios from "../utils/customAxios";

import { styles } from "../styles";

const ProgramPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const location = useLocation();
  const { pathname } = location;
  const path = pathname.split("/").filter((page) => page !== "");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["programStudy", id],

    queryFn: async () => {
      const response = await customAxios.get(`program-studies/${id}`);

      return response?.data?.data || null;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  if (isError) {
    return (
      <ErrorPage
        code="500"
        titleKey="error.500_title"
        defaultTitle="Gagal Memuat Data"
        subtitleKey="error.500_subtitle"
        defaultSubtitle="Terjadi masalah saat terhubung ke server kami. Silakan coba lagi nanti atau kembali ke"
      />
    );
  }

  const renderMarkdown = () => {
    if (isLoading) return <GenericMarkdownLoadingSkeleton />;
    if (!data) return null;

    const { description_en, description_id } = data;
    return (
      <MarkdownParser
        content={currentLanguage === "en" ? description_en : description_id}
      />
    );
  };

  const { name_en, name_id, curriculum_link } = data || {};

  const pageTitle = isLoading
    ? "..."
    : currentLanguage === "en"
      ? name_en
      : name_id;

  // --------- HEADER
  const crumbs = (
    <div className={`${styles.textLRegularM} flex items-center gap-2`}>
      <Link to="/" className="hover:underline">
        {t("header.home")}
      </Link>
      <FaChevronRight className="w-2" />
      {path.map((page, index) => {
        if (index > 0) return (
          <React.Fragment key={index}>
            <p>{pageTitle.split(" ").join("-").toLowerCase()}</p>
            <FaChevronRight
              className={`w-2 ${index === path.length - 1 && "hidden"}`}
            />
          </React.Fragment>
        )
        return (
          <React.Fragment key={index}>
            <p>{page}</p>
            <FaChevronRight
              className={`w-2 ${index === path.length - 1 && "hidden"}`}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
  
  const renderHeader = () => {
    return (
      <div
        style={{
          background: `linear-gradient(to bottom, #920E3450, #920E3475), url(${bgheader})center/cover no-repeat`,
        }}
        className={`${styles.paddingX} w-full h-[300px] relative flex items-center text-white sm:h-40`}
      >
        <img
          src={olheader}
          alt="overlay"
          className="absolute left-0 smd:hidden"
        />
        <img
          src={olheader}
          alt="overlay"
          className="absolute right-0 scale-x-[-1] smd:hidden"
        />
        <div className="flex flex-col gap-2">
          {crumbs}
          <h1 className={`${styles.headingLM}`}>{pageTitle}</h1>
        </div>
      </div>
    );
  };

  //  ---------------

  return (
    <>
      <Navbar />
      {renderHeader()}
      <main
        className={`${styles.paddingX} py-20 flex items-center flex-col gap-10 min-h-[600px]`}
      >
        <section className="max-w-[1152px] w-full flex items-center flex-col space-y-20">
          {renderMarkdown()}

          {curriculum_link && (
            <a
              href={curriculum_link}
              className={`${styles.textLRegular} bg-primary-40 hover:bg-primary-50 text-white w-fit self-center gap-4 px-6 py-4 rounded flex items-center`}
            >
              <p>
                {currentLanguage === "en"
                  ? "Program Study Curriculum"
                  : "Kurikulum Program Studi"}
              </p>
              <FaExternalLinkAlt />
            </a>
          )}
        </section>
      </main>
      <SiluetCard />
      <Footer />
    </>
  );
};

export default ProgramPage;
